<script setup lang="ts">
import { computed, Component } from "vue";
import { first, get } from "lodash-es";

import VisitTinyurl from "@Components/sharing/VisitTinyurl.vue";
import QRDropdown from "@Components/sharing/QRDropdown.vue";
import ShareDropdown from "@Components/sharing/ShareDropdown.vue";
import CopyTinyurl from "@Components/sharing/CopyTinyurl.vue";

import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";
import { Alias } from "@Modules/urls/Data/Models/AliasFactory";
import type { ComponentProps } from "@Modules/shared/interfaces/ComponentProps";

//
// Props
interface Props {
  createdTinyurl: UrlAlias;
}

const props = defineProps<Props>();

//
// Emit
const emit = defineEmits<{
  (e: "on-shorten-another"): void;
}>();

//
// Data
const tinyurl = computed(() => props.createdTinyurl as UrlAlias);

//
// Computed
interface TinyurlAction {
  id: string;
  component: Component;
  props: Record<string, unknown>;
}

// Pairs a component with props that are type-checked against that component's real prop
// contract. The template renders these via <component :is>, which cannot check the
// component/props pairing, so validation happens here at construction time.
function createAction<C extends Component>(
  id: string,
  component: C,
  props: ComponentProps<C>,
): TinyurlAction {
  return { id, component, props: props as Record<string, unknown> };
}

const mainAliases = computed(() => tinyurl.value.aliases.filter((alias: Alias) => alias.is_main));

const tinyurlActions = computed<TinyurlAction[]>(() => {
  const prefix = "form";
  const firstAlias = mainAliases.value[0];
  const url = firstAlias ? decodeURI(firstAlias.tiny_url) : "";

  return [
    createAction("visit-tinyurl", VisitTinyurl, { prefix, url }),
    createAction("download-qr", QRDropdown, { prefix, url }),
    createAction("share-tinyurl", ShareDropdown, { prefix, url }),
    createAction("copy-tinyurl", CopyTinyurl, { prefix, url }),
  ];
});

//
// Methods
function isAliasAffiliated(aliases: Alias[]): boolean {
  return !!get(first(aliases), "affiliates");
}

//
// Exposed Test Vars
defineExpose({
  mainAliases,
  tinyurlActions,
  isAliasAffiliated,
});
</script>

<template>
  <div class="tinyurl-result">
    <div class="form-group mb-3">
      <label for="to-shorten-field">
        <img
          class="mr-2"
          src="@Images/home/url.svg"
          alt=""
        > Your Long URL
      </label>
      <input
        id="to-shorten-field"
        class="form-control form-control-lg text-break text-green"
        readonly
        :value="tinyurl.url"
      >
    </div>
    <div class="form-group mb-3">
      <label>
        <img
          class="mr-2"
          src="@Images/home/magic-wand.svg"
          alt=""
        > TinyURL
      </label>
      <div class="mt-n2">
        <input
          v-for="mainAlias in mainAliases"
          :key="mainAlias.alias"
          data-test-id="dashboard_create_tinyurl_form_created_input"
          class="form-control form-control-lg mt-2 text-break text-green"
          readonly
          :value="decodeURI(mainAlias.tiny_url)"
        >
        <div
          v-if="isAliasAffiliated(mainAliases)"
          class="small"
        >
          {{ $t("tinyurl_affiliated_notice") }}
          <router-link :to="{ name: 'affiliateOptionPop' }">
            <u>{{ $t("learn_more") }}</u>
          </router-link>
        </div>
      </div>
    </div>
    <div class="d-flex flex-wrap mb-3">
      <component
        :is="action.component"
        v-for="action in tinyurlActions"
        :key="action.id"
        v-bind="action.props"
      />
    </div>
    <div class="d-flex flex-wrap-reverse m-n2">
      <div class="flex-grow-1 p-2">
        <button
          data-test-id="dashboard_create_tinyurl_form_shorten_another_btn"
          class="btn btn-xl btn-green btn-block text-nowrap"
          @click="emit('on-shorten-another')"
        >
          Shorten another
        </button>
      </div>
    </div>
  </div>
</template>
